// ServiceOS/frontend/src/components/forms/PublicFormPage.js

import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import api from '../../utils/api';
import FormRenderer from './FormRenderer';
import Loader from '../common/Loader';

const PublicFormPage = () => {
    const { id } = useParams();
    const location = useLocation();
    const [formDefinition, setFormDefinition] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // ⭐ NEW: Added submission state to show the success message
    const [isSubmitted, setIsSubmitted] = useState(false);

    // ⭐ NEW: A 'form' QR campaign's scan-redirect (see qrCampaignController.js's
    // redirectQrScan) lands here with '?source=slug' appended to this same
    // '/forms/:id' URL. Read it once on mount, same lazy-useState pattern
    // already used for the chatbot's sessionId in EmbeddedChatbotPage.js.
    const [qrSource] = useState(() => new URLSearchParams(location.search).get('source'));
    // ⭐ NEW: A review link carries ?jobId=&customerId= - pass them on so a review form
    // opened on this page can still be submitted against the right job.
    const [linkJobId] = useState(() => new URLSearchParams(location.search).get('jobId'));
    const [linkCustomerId] = useState(() => new URLSearchParams(location.search).get('customerId'));

    useEffect(() => {
        const fetchForm = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await api.get(`/public/forms/${id}`);
                setFormDefinition(res.data);
            } catch (err) {
                console.error('Error fetching public form:', err);
                setError(err.response?.data?.message || 'Failed to load form.');
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchForm();
        } else {
            setLoading(false);
            setError('No form ID provided.');
        }
    }, [id]);

    // ⭐ MODIFIED: handleFormSubmission now accepts and sends FormData.
    const handleFormSubmission = async (formData) => {
        try {
            // When submitting FormData with files, we use the raw fetch and rely on the browser to set 
            // the 'Content-Type: multipart/form-data' header correctly, including the boundary.
            // Note: If 'api.post' uses axios/instance, ensure it's configured for FormData.
            const response = await api.post(`/public/forms/${id}/submit`, formData, {
                // Ensure the Content-Type header is explicitly removed so the browser can set the boundary
                headers: {
                    'Content-Type': undefined,
                }
            });

            // ⭐ NEW: Custom redirect on success (same as the Smart Booking Form).
            // If the admin set a Thank You / Redirect URL on this form, send the
            // visitor there instead of showing the built-in success screen - this
            // is what lets a dedicated thank-you page fire Google Ads / analytics
            // conversion tags. Normalise a bare domain to https:// so a value like
            // "yourwebsite.com/thank-you" isn't treated as a relative path.
            const rawRedirect = (formDefinition?.redirectUrl || '').trim();
            if (rawRedirect) {
                const target = /^https?:\/\//i.test(rawRedirect) ? rawRedirect : `https://${rawRedirect}`;
                // Navigate the top-level window so it works both standalone and when the
                // form is embedded in an iframe on the customer's own website.
                try {
                    window.top.location.href = target;
                } catch (e) {
                    // Cross-origin parent access can throw in some embeds - fall back.
                    window.location.href = target;
                }
                return { success: true, message: response.data.message };
            }

            // ⭐ No redirect configured: show the built-in success screen as before.
            setIsSubmitted(true);
            return { success: true, message: response.data.message };
        } catch (err) {
            console.error('Public form submission failed:', err);
            // Handle errors thrown by the API instance
            throw new Error(err.response?.data?.message || 'Submission failed.');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <Loader />
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-6">
                <div className="bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-600 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg max-w-md text-center">
                    <p className="font-bold">Error Loading Form</p>
                    <p className="text-sm">{error}</p>
                </div>
            </div>
        );
    }
    
    // ⭐ NEW: Sleek Success UI preventing the "Funnel into nothing" issue
    if (isSubmitted) {
        return (
            <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
                <div className="w-full max-w-md bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl rounded-3xl shadow-2xl p-8 sm:p-10 text-center animate-in zoom-in-95 fade-in duration-500 border border-slate-200 dark:border-slate-800">
                    <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 text-green-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
                        <span className="text-4xl font-bold">✓</span>
                    </div>
                    <h2 className="text-3xl font-black text-slate-900 dark:text-white mb-4 tracking-tight">Thank You!</h2>
                    <p className="text-slate-500 dark:text-slate-400 text-lg font-medium">
                        {formDefinition?.successMessage || "Your submission has been received successfully."}
                    </p>
                    {/* ⭐ NEW: Review forms invite every reviewer to also leave a public review */}
                    {(formDefinition?.reviewLinks?.googleReviewLink || formDefinition?.reviewLinks?.trustpilotLink) && (
                        <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-800">
                            <p className="text-slate-700 dark:text-slate-300 font-semibold mb-4">
                                Could you also share your experience publicly? It really helps us.
                            </p>
                            <div className="flex flex-col gap-3">
                                {formDefinition.reviewLinks.googleReviewLink && (
                                    <a
                                        href={formDefinition.reviewLinks.googleReviewLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl bg-blue-600 hover:bg-blue-700 text-white font-bold shadow-lg shadow-blue-500/25 transition-colors"
                                    >
                                        Leave a Google review
                                    </a>
                                )}
                                {formDefinition.reviewLinks.trustpilotLink && (
                                    <a
                                        href={formDefinition.reviewLinks.trustpilotLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl bg-[#00B67A] hover:bg-[#00a36d] text-white font-bold shadow-lg transition-colors"
                                    >
                                        Review us on Trustpilot
                                    </a>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // ⭐ AESTHETIC UPGRADE: Moved away from flat bg-gray-100 and stripped the wrapper's white box to let FormRenderer's glassmorphism shine
    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4 sm:p-8">
            <div className="w-full max-w-4xl">
                <FormRenderer formDefinition={formDefinition} onSubmit={handleFormSubmission} isPreview={false} qrSource={qrSource} jobId={linkJobId} customerId={linkCustomerId} />
            </div>
        </div>
    );
};

export default PublicFormPage;